import { useEffect, useState } from 'react';
import { useQuery, gql } from '@apollo/client';
import { Issue } from '../../../../constants';
import config from '../../../../config';
import { useCache } from '../../../../App.hooks';

export function useGetIssues(sectionId: number): GetIssuesResponse {
	const [issues, setIssues] = useState<Array<Issue>>([]);

	const fetchPolicy = useCache(
		`section:${sectionId}:issues`,
		config?.app?.cache?.ttl?.getAllIssues,
		{
			id: 'ROOT_QUERY',
			fieldName: `section({"id":${sectionId}})`,
			broadcast: false,
		},
	);

	const { loading, error, data } = useQuery(GET_ISSUES_IN_SECTION, {
		fetchPolicy,
		variables: { id: sectionId },
	});

	useEffect(() => {
		if (loading) return;
		if (error) {
			console.error(error);
			return;
		}
		if (!data) return;
		const { section } = data;
		if (section.issues) {
			const issues = JSON.parse(JSON.stringify(section.issues)).sort(
				sortIssuesReleaseDate,
			);
			setIssues(issues);
		}
	}, [loading, error, data]);

	return {
		isLoading: loading,
		error: error ? new Error(error.message) : null,
		issues,
	};
}

function sortIssuesReleaseDate(a: Issue, b: Issue) {
	if (a.releaseDate && b.releaseDate) {
		return (
			new Date(`${b.releaseDate}`).getTime() -
			new Date(`${a.releaseDate}`).getTime()
		);
	}
	return (
		new Date(`${b.updated}`).getTime() - new Date(`${a.updated}`).getTime()
	);
}

interface GetIssuesResponse {
	isLoading: boolean;
	error: Error | null;
	issues: Array<Issue>;
}

export const GET_ISSUES_IN_SECTION = gql`
	query GetSection($id: ID!) {
		section(id: $id) {
			id
			position
			name
			issues {
				id
				position
				name
				description
				cover
				externalUrl
				price
				features
				currency
				slug
				data
				releaseDate
				features
				updated
				isPaid
				created
				isPurchased
			}
		}
	}
`;
